import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Phone, Truck } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ y: 40, opacity: 0 }}
    whileInView={{ y: 0, opacity: 1 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const LOCAL_AREAS = [
  "Lebanon", "Mt. Juliet", "Watertown", "Wilson County", "Nashville", "Murfreesboro",
  "Hendersonville", "Gallatin", "Mount Juliet", "Smyrna", "Hermitage", "Donelson",
];

export default function ServiceAreas() {
  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="service-areas-page">
      <div className="noise-overlay" />
      <header className="bg-[#F4F4F5]/95 backdrop-blur-md border-b border-zinc-300 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="sa-logo" className="flex items-center gap-3">
            <img src="/logo192.png" alt="Anchor Moving logo" className="w-9 h-9 object-cover" />
            <span className="font-display uppercase text-xl tracking-wide leading-none">Anchor Moving</span>
          </Link>
          <Link
            to="/"
            data-testid="sa-back-link"
            className="inline-flex items-center gap-2 font-sub text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 hover:text-[#0057FF] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-20 sm:py-28">
        <Reveal>
          <p className="font-sub text-xs tracking-[0.3em] uppercase font-bold text-[#0057FF]">Service Areas</p>
          <h1 className="font-display uppercase text-4xl sm:text-6xl leading-[0.9] tracking-tight mt-4 max-w-4xl" data-testid="sa-heading">
            Local In Tennessee. <span className="text-[#0057FF]">Long Distance Nationwide.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-600 text-base md:text-lg leading-relaxed">
            Anchor Moving is a moving company stationed in the heart of Lebanon, Tennessee. We run local moves
            across Middle Tennessee and long-distance moves to every state in the country — same crew,
            same care, same flat pricing either way.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-16">
          <h2 className="font-display uppercase text-2xl sm:text-3xl">Middle Tennessee Local Moves</h2>
          <p className="mt-3 text-zinc-600 text-base max-w-2xl">
            Same-day crews for homes, apartments, and offices across Wilson County and the greater Nashville area:
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-zinc-300 border border-zinc-300" data-testid="sa-areas-grid">
            {LOCAL_AREAS.map((area) => (
              <div key={area} className="bg-white px-5 py-5 flex items-center gap-3 hover:bg-zinc-50 transition-colors" data-testid={`sa-area-${area.toLowerCase().replace(/[\s.]+/g, "-")}`}>
                <MapPin className="w-4 h-4 text-[#0057FF] shrink-0" strokeWidth={2} />
                <span className="font-sub text-sm font-bold uppercase tracking-wider text-zinc-800">{area}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-16 bg-zinc-900 text-white p-10 sm:p-14 shadow-[8px_8px_0px_0px_#0057FF]" data-testid="sa-nationwide">
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <Truck className="w-12 h-12 text-[#0057FF] shrink-0" strokeWidth={1.5} />
            <div>
              <h2 className="font-display uppercase text-2xl sm:text-3xl">Moving Out Of State? We Go There.</h2>
              <p className="mt-3 text-zinc-400 text-base leading-relaxed max-w-2xl">
                Long-distance moves from Tennessee to anywhere in the United States — dedicated trucks,
                GPS-tracked loads, and guaranteed delivery windows. Call dispatch and we'll map your route
                and give you a flat quote the same day.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="mt-16 flex flex-wrap items-center gap-6">
          <Link to="/#quote" data-testid="sa-quote-button">
            <span className="inline-flex items-center gap-2 bg-[#0057FF] text-white font-sub font-bold uppercase tracking-[0.15em] text-sm px-8 py-4 rounded-none shadow-[4px_4px_0px_0px_#09090B] transition-[transform,box-shadow] duration-150 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[7px_7px_0px_0px_#09090B]">
              Get A Same Day Quote
            </span>
          </Link>
          <a href="tel:+12702255228" data-testid="sa-call-link" className="inline-flex items-center gap-3 font-sub font-bold text-lg text-zinc-900 hover:text-[#0057FF] transition-colors">
            <Phone className="w-5 h-5" /> 270-225-5228
          </a>
        </Reveal>
      </main>
    </div>
  );
}
